/**
 * Sidebar premium do wizard de templates WABA
 * Layout igual ao ImportListWizard com gradiente azul escuro
 */

import { Settings, MessageSquare, Check, FileText, Hash, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WizardStep, StepDefinition } from './types';

// Definição dos steps do wizard de templates
const STEPS: (StepDefinition & { icon: React.ElementType })[] = [
  { 
    key: 'config', 
    label: 'Configurações', 
    icon: Settings,
    description: 'Inbox, nome, categoria e idioma' 
  },
  { 
    key: 'message', 
    label: 'Mensagem', 
    icon: MessageSquare,
    description: 'Header, body, footer e botões' 
  },
];

interface TemplateWizardSidebarProps {
  currentStep: WizardStep;
  onStepClick: (step: WizardStep) => void;
  isStepCompleted: (step: WizardStep) => boolean;
  isStepDisabled: (step: WizardStep) => boolean;
  summary: {
    inboxName?: string;
    category?: string;
    language?: string;
    buttonCount: number;
    hasHeader: boolean;
  };
}

export function TemplateWizardSidebar({
  currentStep,
  onStepClick,
  isStepCompleted,
  isStepDisabled,
  summary,
}: TemplateWizardSidebarProps) {
  const currentStepIndex = STEPS.findIndex(s => s.key === currentStep);
  
  return (
    <aside 
      className="hidden md:flex flex-col relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
      }}
    >
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
      </div>
      
      {/* Conteúdo da sidebar */}
      <div className="relative z-10 flex flex-col h-full p-5">
        
        {/* Header da sidebar */}
        <div className="mb-6">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-white">Modelo WABA</h2>
          <p className="text-sm text-white/60 mt-1">
            Etapa {currentStepIndex + 1} de {STEPS.length}
          </p>
        </div>
        
        {/* Lista de Steps */}
        <nav className="flex-1 space-y-1">
          {STEPS.map((step, index) => {
            const isCompleted = isStepCompleted(step.key);
            const isActive = step.key === currentStep;
            const isPending = !isCompleted && !isActive;
            const isDisabled = isStepDisabled(step.key);
            const Icon = step.icon;
            
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => !isDisabled && onStepClick(step.key)}
                disabled={isDisabled}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200",
                  isActive && "bg-white/10 border border-white/20",
                  isCompleted && !isActive && "hover:bg-white/5 cursor-pointer",
                  isDisabled && "opacity-50 cursor-not-allowed",
                  !isActive && !isDisabled && "hover:bg-white/5"
                )}
              >
                {/* Círculo indicador */}
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 border-2",
                  isCompleted && "bg-green-500 border-green-500",
                  isActive && "bg-white border-white",
                  isPending && "bg-transparent border-white/30"
                )}>
                  {isCompleted ? (
                    <Check className="h-4 w-4 text-white" />
                  ) : (
                    <Icon className={cn(
                      "h-4 w-4",
                      isActive ? "text-primary" : "text-white/60"
                    )} />
                  )}
                </div>
                
                {/* Texto do step */}
                <div className="flex-1 min-w-0">
                  <span className={cn(
                    "block text-sm font-medium truncate",
                    isActive ? "text-white" : "text-white/80"
                  )}>
                    {step.label}
                  </span>
                  <span className="block text-xs text-white/40 truncate">
                    {step.description}
                  </span>
                </div>
              </button>
            );
          })}
        </nav>
        
        {/* Resumo no rodapé */}
        <div className="mt-auto pt-4 border-t border-white/10">
          <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">
            Resumo
          </h3>
          <div className="space-y-2">
            {/* Inbox selecionada */}
            {summary.inboxName && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Inbox</span>
                </div>
                <span className="text-sm font-medium text-white truncate max-w-[120px]">
                  {summary.inboxName}
                </span>
              </div>
            )}
            
            {/* Categoria */}
            {summary.category && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Categoria</span>
                </div>
                <span className="text-sm font-medium text-white">
                  {summary.category}
                </span>
              </div>
            )}
            
            {/* Botões */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/80">Botões</span>
              </div>
              <span className="text-sm font-medium text-white">
                {summary.buttonCount}
              </span>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
